import React from 'react';
import { Plus, Edit, Trash2, Search, Church } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { igrejaService, campoService } from '../services/api';

const Igrejas = () => {
  const { data: igrejas, loading, error, create, update, remove } = useCrud(igrejaService, 'igrejas');
  const { data: campos } = useCrud(campoService, 'campos');
  const modal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  const validationRules = {
    nome: { required: true, label: 'Nome', minLength: 2, maxLength: 100 },
    campo_id: { required: true, label: 'Campo' },
    endereco: { required: true, label: 'Endereço', minLength: 5 }
  };

  const form = useForm(
    { nome: '', campo_id: '', endereco: '', telefone: '' },
    validationRules
  );

  const filteredIgrejas = igrejas.filter(igreja =>
    igreja.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    igreja.Campo?.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Nova Igreja' });
  };

  const handleEdit = (igreja) => {
    form.reset({
      nome: igreja.nome || '',
      campo_id: igreja.campo_id || '',
      endereco: igreja.endereco || '',
      telefone: igreja.telefone || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Igreja', id: igreja.id });
  };

  const handleDelete = async (igreja) => {
    if (window.confirm(`Deseja realmente excluir a igreja "${igreja.nome}"?`)) {
      await remove(igreja.id);
    }
  };

  const handleSubmit = async (values) => {
    const result = modal.data.mode === 'create' 
      ? await create(values)
      : await update(modal.data.id, values);

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Igrejas</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Nova Igreja
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar igrejas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
        </div>
      </div>

      <div className="card">
        {filteredIgrejas.length === 0 ? (
          <div className="empty-state">
            <Church className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhuma igreja encontrada</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando uma nova igreja.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Nova Igreja
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Campo</th>
                  <th>Endereço</th>
                  <th>Telefone</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredIgrejas.map((igreja) => (
                  <tr key={igreja.id}>
                    <td className="font-medium">{igreja.nome}</td>
                    <td>{igreja.Campo?.nome || 'N/A'}</td>
                    <td>{igreja.endereco}</td>
                    <td>{igreja.telefone || '-'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(igreja)}
                          className="btn btn-sm btn-secondary"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(igreja)}
                          className="btn btn-sm btn-danger"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">×</button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  value={form.values.nome}
                  onChange={(e) => form.setValue('nome', e.target.value)}
                  onBlur={() => form.setTouched('nome')}
                  className={`form-input ${form.errors.nome && form.touched.nome ? 'border-red-500' : ''}`}
                  placeholder="Digite o nome da igreja"
                />
                {form.errors.nome && form.touched.nome && (
                  <div className="form-error">{form.errors.nome}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Campo *</label>
                <select
                  value={form.values.campo_id}
                  onChange={(e) => form.setValue('campo_id', e.target.value)}
                  onBlur={() => form.setTouched('campo_id')}
                  className={`form-select ${form.errors.campo_id && form.touched.campo_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione um campo</option>
                  {campos.map((campo) => (
                    <option key={campo.id} value={campo.id}>
                      {campo.nome}
                    </option>
                  ))}
                </select>
                {form.errors.campo_id && form.touched.campo_id && (
                  <div className="form-error">{form.errors.campo_id}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Endereço *</label>
                <input
                  type="text"
                  value={form.values.endereco}
                  onChange={(e) => form.setValue('endereco', e.target.value)}
                  onBlur={() => form.setTouched('endereco')}
                  className={`form-input ${form.errors.endereco && form.touched.endereco ? 'border-red-500' : ''}`}
                  placeholder="Digite o endereço da igreja"
                />
                {form.errors.endereco && form.touched.endereco && (
                  <div className="form-error">{form.errors.endereco}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Telefone</label>
                <input
                  type="text"
                  value={form.values.telefone}
                  onChange={(e) => form.setValue('telefone', e.target.value)}
                  className="form-input"
                  placeholder="Digite o telefone da igreja"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Igrejas;